/**
 * Canonical relative-time formatting from ISO timestamps (display-time i18n).
 * @param {string|Date} value ISO-8601 or parseable instant
 * @param {(key: string, vars?: object) => string} t
 * @returns {string}
 */
export function formatRelativeTimeFromIso(value, t) {
  if (!value || !t) return '';
  const then = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(then.getTime())) return '';

  const diffMs = Date.now() - then.getTime();
  if (diffMs < 0) return t('home.newsAgo.justNow');

  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t('home.newsAgo.justNow');
  if (mins < 60) return t('home.newsAgo.minutes', { n: mins });

  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('home.newsAgo.hours', { n: hours });

  const days = Math.floor(hours / 24);
  if (days < 7) return t('home.newsAgo.days', { n: days });
  if (days < 30) return t('home.newsAgo.weeks', { n: Math.floor(days / 7) });
  if (days < 365) return t('home.newsAgo.months', { n: Math.floor(days / 30) });
  return t('home.newsAgo.years', { n: Math.floor(days / 365) });
}

/** @param {string} value */
function parseInstant(value) {
  const s = String(value ?? '').trim();
  if (!s) return null;

  if (
    /전$/.test(s)
    || /\sago$/i.test(s)
    || s === '어제'
    || /^yesterday$/i.test(s)
    || s === '방금'
    || s === '방금 전'
    || /^just now$/i.test(s)
    || /^끌올\s/.test(s)
  ) {
    return null;
  }

  if (!/^\d{4}-\d{2}-\d{2}/.test(s) && !/^\d{10,13}$/.test(s)) return null;

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Legacy seed/dummy strings (e.g. "2일 전") — backward compat only.
 * @param {string} s
 * @param {(key: string, vars?: object) => string} t
 */
function formatLegacyRelativeTime(s, t) {
  if (s === '어제' || /^yesterday$/i.test(s)) {
    return t('home.listingTime.yesterday');
  }

  let m = s.match(/^(\d+)\s*일\s*전$/);
  if (m) return t('home.newsAgo.days', { n: Number(m[1]) });

  m = s.match(/^(\d+)\s*d(?:ays?)?\s*ago$/i);
  if (m) return t('home.newsAgo.days', { n: Number(m[1]) });

  m = s.match(/^(\d+)\s*시간\s*전$/);
  if (m) return t('home.newsAgo.hours', { n: Number(m[1]) });

  m = s.match(/^(\d+)\s*h(?:ours?)?\s*ago$/i);
  if (m) return t('home.newsAgo.hours', { n: Number(m[1]) });

  m = s.match(/^(\d+)\s*분\s*전$/);
  if (m) return t('home.newsAgo.minutes', { n: Number(m[1]) });

  m = s.match(/^(\d+)\s*m(?:ins?)?\s*ago$/i);
  if (m) return t('home.newsAgo.minutes', { n: Number(m[1]) });

  m = s.match(/^끌올\s*(\d+)\s*분\s*전$/);
  if (m) return t('home.listingTime.bumpMinutes', { n: Number(m[1]) });

  m = s.match(/^(\d+)\s*주\s*전$/);
  if (m) return t('home.newsAgo.weeks', { n: Number(m[1]) });

  m = s.match(/^(\d+)\s*w(?:eeks?)?\s*ago$/i);
  if (m) return t('home.newsAgo.weeks', { n: Number(m[1]) });

  m = s.match(/^(\d+)\s*개월\s*전$/);
  if (m) return t('home.newsAgo.months', { n: Number(m[1]) });

  m = s.match(/^(\d+)\s*mo(?:nths?)?\s*ago$/i);
  if (m) return t('home.newsAgo.months', { n: Number(m[1]) });

  m = s.match(/^(\d+)\s*년\s*전$/);
  if (m) return t('home.newsAgo.years', { n: Number(m[1]) });

  m = s.match(/^(\d+)\s*y(?:ears?)?\s*ago$/i);
  if (m) return t('home.newsAgo.years', { n: Number(m[1]) });

  if (s === '방금' || s === '방금 전' || /^just now$/i.test(s)) {
    return t('home.newsAgo.justNow');
  }

  return s;
}

/**
 * Display relative time: ISO timestamps (primary) or legacy pre-formatted strings (seed/dummy).
 * @param {string} [value]
 * @param {(key: string, vars?: object) => string} t
 */
export function formatDisplayTime(value, t) {
  if (value == null || value === '') return '';
  const instant = parseInstant(value);
  if (instant) return formatRelativeTimeFromIso(instant, t);
  return formatLegacyRelativeTime(String(value).trim(), t);
}
